// The enemies stream's share of the sim, the handle and the harness (gate B4), on the joined fight (./fight.ts): the
// arena scene (`?scene=arena`), its invariants (never more attackers than the token cap; enemies and hero finite and
// above ground), its planted-defect switches, and the handle keys under `__rpg.arena`. `?difficulty=easy|normal|hard`
// picks the difficulty at boot (else the settings' `accessibility.difficulty`); `?director=off` starts without waves;
// `?navDebug` shows the navmesh. The valley's fight (src/valley/fight.ts) shares the invariants and the state summary.
import type * as THREE from "three";
import { finite, notBelowGround, type Invariant } from "@rpg/harness";
import { NO_INPUT } from "@rpg/controller";
import type { Runtime } from "@rpg/runtime";
import type { SimHost } from "../sim/host";
import type { SpikeContent } from "../content";
import { readTuning, type TuningLookup } from "../valley/tuning";
import { BRAIN_FAULTS } from "@rpg/ai";
import { ARENA_FAULTS, createArena, type ArenaFaultName, type ArenaRig } from "./arena";
import { HERO_ID, readFightContent, type Fight } from "./fight";
import { ragdollInvariants } from "../bodies";
import { attachFightBot, runFight } from "./bot";
import { mountArenaView, type ArenaFrame, type ArenaView } from "./view";

export { ARENA_FAULTS, type ArenaFaultName } from "./arena";
export const ARENA_FAULT_NAMES = Object.keys(ARENA_FAULTS) as ArenaFaultName[];

/** The arena scene from the content and the tuning table. */
export function arenaScene(get: TuningLookup, rowIds: readonly string[], content: SpikeContent, o: { difficulty?: string; director?: boolean; runtime?: Runtime } = {}) {
  const runtime = o.runtime;
  return createArena(readTuning(get, rowIds), readFightContent(content), {
    ...(o.difficulty ? { difficulty: o.difficulty } : {}),
    ...(o.director !== undefined ? { director: o.director } : {}),
    ...(runtime ? { slowMo: (scale: number, s: number, ease: number) => runtime.clock.slowMo(scale, s, ease) } : {}),
  });
}

/** Switch a fight fault on or off, in the record and in the live objects it lives beside. */
export function setArenaFault(fight: Fight | null, name: ArenaFaultName, on: boolean): void {
  ARENA_FAULTS[name] = on;
  if (!fight) return;
  if (name === "tokenCapRemoved") fight.tokens.faults.capRemoved = on;
  if (name === "crowdNoReseat") fight.crowd.faults.noReseat = on;
  if (name === "ignoreTokens") BRAIN_FAULTS.ignoreTokens = on;
}

/** A fight's invariants (the arena's and the valley's), added to the harness's watch after every step. */
export function fightInvariants(fight: () => Fight, terrainAt: (x: number, z: number) => number): Invariant[] {
  return [
    // gate B4: the token cap holds on every step (planted defect: tokenCapRemoved)
    () => {
      const f = fight();
      const n = f.attackers().length;
      return n > f.tokens.cap ? `more attackers than the token cap (${n} > ${f.tokens.cap})` : null;
    },
    finite("fight", () => fight().state().enemies.flatMap((e) => [...e.pos, ...e.vel, e.facing])),
    () => {
      for (const e of fight().state().enemies) if (e.alive && e.pos[1] < terrainAt(e.pos[0], e.pos[2]) - 0.3) return `enemy ${e.id} below ground`;
      return null;
    },
    // combat's numbers stay in range (the dojo's check, for the fight's fighters)
    () => {
      const f = fight();
      for (const id of f.combat.fighters()) {
        const v = f.combat.state(id);
        if (![v.hp, v.hpMax, v.poise, v.stamina, v.energy].every(Number.isFinite)) return `non-finite combat state (fighter ${id})`;
        if (v.hp < 0 || v.hp > v.hpMax + 1e-9) return `fighter ${id} hp ${v.hp} outside 0..${v.hpMax}`;
        if (!v.alive && v.hp !== 0) return `fighter ${id} defeated with hp ${v.hp}`;
      }
      return null;
    },
    // the ragdolls (gate B5's checks, in the fight)
    ...ragdollInvariants(() => fight().bodies, () => fight().collision, terrainAt),
  ];
}

/** The arena's invariants. */
export function arenaInvariants(rig: () => ArenaRig): Invariant[] {
  return [
    ...fightInvariants(() => rig().fight, (x, z) => rig().terrainAt(x, z)),
    finite("arena hero", () => {
      const h = rig().hero();
      return [h.feet.x, h.feet.y, h.feet.z];
    }),
    notBelowGround("hero", () => rig().hero().feet, (x, z) => rig().terrainAt(x, z), { clearance: -0.05, detail: false }),
  ];
}

/** A summary of a fight for `state()` and specs. */
export function fightSummary(f: Fight) {
  const s = f.state();
  const hero = f.view(HERO_ID);
  return {
    difficulty: s.difficulty,
    cap: f.tokens.cap,
    steps: s.steps,
    heroHp: hero?.hp ?? 0,
    respawns: s.hero.respawns,
    attackers: f.attackers(),
    holders: f.tokens.holders(),
    engaged: f.engaged(),
    ragdolls: f.bodies.state().ragdolls.length,
    director: s.director ? { phase: s.director.phase, wave: s.director.wave, started: s.director.started, alive: s.director.alive.length, intensity: s.director.intensity } : null,
    directorOn: s.directorOn,
    enemies: s.enemies.map((e) => {
      const v = f.view(e.id);
      return {
        id: e.id,
        kind: e.kind,
        phase: e.phase,
        alive: e.alive,
        x: e.pos[0],
        y: e.pos[1],
        z: e.pos[2],
        speed: Math.hypot(e.vel[0], e.vel[2]),
        facing: e.facing,
        level: e.percept.level,
        awareness: e.percept.awareness,
        sees: e.percept.sees,
        hp: v?.hp ?? 0,
        hpMax: v?.hpMax ?? 0,
        move: v?.move ? { id: v.move.id, phase: v.move.phase } : null,
        token: f.tokens.holds(e.id),
        weapon: v?.weapon ?? null,
        moveset: e.phase > 1 ? (f.content.kinds[e.kind]!.phases[e.phase - 2]?.moveset ?? {}) : f.content.kinds[e.kind]!.moveset,
        trace: e.tree.trace.filter((l) => !l.startsWith("?") && !l.startsWith("sequence") && !l.startsWith("selector")),
      };
    }),
  };
}

/** A summary of the arena for `state()` and specs. */
export function arenaState(r: ArenaRig) {
  const h = r.hero();
  const sum = fightSummary(r.fight);
  return { ...sum, hero: { x: h.feet.x, y: h.feet.y, z: h.feet.z, hp: sum.heroHp, respawns: sum.respawns } };
}

/** Handle keys for a fight (the arena's and the valley's). */
export function fightHandle(fight: () => Fight, runtime: Runtime) {
  return {
    /** Spawn an enemy (snapped to the navmesh); `alert` starts it hunting. Returns its id. */
    spawn: (kind: string, x: number, z: number, o?: { alert?: boolean; facing?: number }) => fight().spawn(kind, x, z, o),
    /** A fresh fight: enemies cleared, the hero healed at its start, the director reset (and on or off). */
    reset: (o?: { difficulty?: string; director?: boolean }) => fight().reset(o),
    /** The difficulty table's rows, and the one in play. */
    difficulty: () => ({ current: fight().state().difficulty, table: structuredClone(fight().content.difficulty) }),
    /** Play at a difficulty: a fresh fight when it differs from the one in play (the settings screen's choice). */
    useDifficulty: (id: string) => {
      if (id !== fight().state().difficulty && fight().content.difficulty[id]) fight().reset({ difficulty: id });
    },
    /** Turn the director's waves on or off. */
    director: (on: boolean) => fight().setDirector(on),
    /** Take `amount` of a fighter's health, leaving at least 1 (specs push a boss into its next phase). */
    damage: (id: number, amount: number) => {
      const f = fight();
      const st = f.combatState().fighters[id];
      if (!st) return;
      f.combat.abilities.setBase(st.stats, "hp", Math.max(1, f.combat.abilities.base(st.stats, "hp") - amount));
    },
    /** The last `n` combat and perception events. */
    events: (n = 50) => fight().events.slice(-n),
    /** Totals since the last reset (damage, hits and moves by id, defeats by kind). */
    tally: () => structuredClone(fight().tally),
    /** The token pool's counts since boot. */
    tokens: () => fight().tokens.stats(),
    /** The bodies module's state: tracks and live ragdolls (their phases and ages). */
    bodies: () => fight().bodies.state().ragdolls.map((r) => ({ entity: r.entity, card: r.card, phase: r.state.phase, age: r.state.age })),
    /** Advance `seconds` of sim time without drawing. */
    run: (seconds: number) => {
      for (let t = 0; t < seconds - 1e-9; t += 1) runtime.advance(Math.min(1, seconds - t) * 1000, { draw: false });
    },
  };
}

/** The handle keys the arena adds (`__rpg.arena`). */
export function arenaHandle(rig: () => ArenaRig, host: SimHost, runtime: Runtime, view: () => ArenaView | null) {
  let bot: { stop(): void } | null = null;
  let held = false;
  return {
    arena: {
      ...fightHandle(() => rig().fight, runtime),
      state: () => arenaState(rig()),
      /** A seeded bot fight against the waves for `minutes` of sim time, logging attackers per step (gate B4). */
      fight: (o?: { seed?: number; minutes?: number; difficulty?: string }) => runFight(rig(), runtime, o),
      /** The fight bot drives the hero from now on (live viewing, shots), or stops. */
      bot: (on: boolean, seed?: number) => {
        bot?.stop();
        bot = on ? attachFightBot(rig(), seed === undefined ? {} : { seed }) : null;
      },
      /** Hold the hero still (shots), or let the input drive it again. */
      holdHero: (on: boolean) => {
        held = on;
        const r = rig();
        r.heroInput = on ? NO_INPUT : null;
        if (on) r.drive = () => void (r.heroInput = NO_INPUT);
        else if (r.drive && !bot) r.drive = null;
      },
      held: () => held,
      /** Teleport the hero (at rest). */
      warpHero: (x: number, z: number, yaw?: number) => rig().warpHero(x, z, yaw),
      /** Ask the hero's side of the combat port for an intent at the next step (specs drive the hero's swings and
       *  dodges). */
      heroIntent: (kind: "attack" | "dodge" | "skill" | "big" | "block" | "blockRelease", dir?: [number, number, number]) => void (rig().heroIntent = dir ? { kind, dir } : { kind }),
      /** The navmesh: its build report, a path query, the crowd's counts. */
      nav: {
        report: () => rig().nav.report(),
        path: (from: [number, number, number], to: [number, number, number]) => rig().nav.path(from, to),
        crowd: () => rig().fight.crowd.stats(),
      },
      /** Frame the camera for a shot (null: follow the hero). */
      frame: (f: ArenaFrame | null) => view()?.frame(f),
      /** Show the navmesh debug view. */
      navDebug: (on: boolean) => view()?.navDebug(on),
      view: () => view()?.state() ?? null,
      /** The state hash's parts (the fight's modules and the crowd are in `kernel`). */
      hash: () => host.hashParts(),
    },
  };
}

/** Mount the arena's dressed view (when there's a renderer). */
export function mountArena(o: Parameters<typeof mountArenaView>[0]): ArenaView {
  return mountArenaView(o);
}
